import React, { useMemo } from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useConsultation } from "../../ConsultationContext";
import { workoutItemType } from "../../../../providers/dataPlatformReportTypes";
import SectionHeader from "../shared/SectionHeader";

/**
 * Section 3 · Sub 3.3 — Workout plan grid. Visual reference: Figma node
 * 14550:86819.
 *
 * Card anatomy:
 *   ┌───────────────────────────────────────┐
 *   │ {category}                            │
 *   │ {name}                                │
 *   │ [cadence pill] [duration pill]        │
 *   └───────────────────────────────────────┘
 *
 * Real data: `dataPlatformServer.programming.workoutPlan.workout[]`
 *   - title         → category ("Cardio" / "Interval" / "Resistance" / "Endurance")
 *   - detail.name   → base name ("Running", "HIIT" …)
 *   - detail.sub_type → modifier ("Zone 4") — joined with " - " when present
 *   - detail.count  → cadence pill ("2 per week")
 *   - detail.duration → duration pill ("40 min")
 *
 * Pill backgrounds are category-coded (Endurance/Cardio = D[10] pink, Interval
 * = W[10] yellow, Resistance = P[10] blue). Falls back to neutral N[30] for
 * any other category.
 */
type Card = {
  id: string;
  category: string;
  name: string;
  /**
   * Resistance-only modifier (body region — "Hypertrophy", "Strength"). The
   * platform's Workout Plan tab shows this as its own chip, so we mirror that
   * here: rendered as the first pill when present, instead of being inlined
   * into the workout name.
   */
  subType: string;
  cadence: string;
  duration: string;
  kcal: string;
};

const pillBg = (theme: Theme, category: string): string => {
  switch (category?.toLowerCase()) {
    case "endurance":
    case "cardio":
      return theme.colorTokens.D[10];
    case "interval":
      return theme.colorTokens.W[10];
    case "resistance":
      return theme.colorTokens.P[10];
    default:
      return theme.colorTokens.N[30];
  }
};

const TrainingCard: React.FC<{ card: Card }> = ({ card }) => {
  const theme: Theme = useTheme();
  const pillColor = pillBg(theme, card.category);
  return (
    <Box sx={styles.card(theme)}>
      <Box sx={styles.body}>
        <Box sx={styles.category(theme)}>{card.category}</Box>
        <Box sx={styles.name(theme)}>{card.name}</Box>
        <Box sx={styles.pillsRow}>
          {card.subType && (
            <Box
              component="span"
              sx={{ ...styles.pill(theme), backgroundColor: pillColor }}
            >
              {card.subType}
            </Box>
          )}
          {card.cadence && (
            <Box
              component="span"
              sx={{ ...styles.pill(theme), backgroundColor: pillColor }}
            >
              {card.cadence}
            </Box>
          )}
          {card.duration && (
            <Box
              component="span"
              sx={{ ...styles.pill(theme), backgroundColor: pillColor }}
            >
              {card.duration}
            </Box>
          )}
          {card.kcal && (
            <Box
              component="span"
              sx={{ ...styles.pill(theme), backgroundColor: pillColor }}
            >
              {card.kcal}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Clean up the raw `detail.title` shipped by the backend so it reads like
 * the Figma name ("Running - Zone 2" instead of "Running-None-Zone 2"):
 * split on hyphen, drop empty / "None" segments, rejoin with " - ".
 */
const cleanWorkoutName = (raw: string): string => {
  if (!raw) return "";
  return raw
    .split("-")
    .map((s) => s.trim())
    .filter((s) => s && s !== "None")
    .join(" - ");
};

const buildCards = (workouts: workoutItemType[] | undefined): Card[] => {
  if (!workouts) return [];
  return workouts.map((w, i) => {
    // `workoutDetailsType` only types title/type/count/duration/kcal, but the
    // real payload also ships `sub_type` (resistance body-part qualifier).
    // Extend the existing type rather than casting to `Record<string, string>`
    // (which clashes with `type: number` on `workoutDetailsType`).
    const detail = (w.detail || {}) as typeof w.detail & {
      sub_type?: string;
    };
    const rawTitle = detail.title || w.title;
    const name = cleanWorkoutName(rawTitle);
    const rawSub = detail.sub_type;
    // sub_type is only meaningful for Resistance (type 2); other categories
    // ship "None" or omit it, which the platform doesn't render.
    const subType =
      w.type === 2 && rawSub && rawSub !== "None" ? rawSub : "";
    return {
      id: `wk-${i}`,
      category: w.title,
      name,
      subType,
      cadence: detail.count ?? "",
      duration: detail.duration ?? "",
      kcal: detail.kcal ?? "",
    };
  });
};

const TrainingPanel: React.FC = () => {
  const theme: Theme = useTheme();
  const { data } = useConsultation();
  const cards = useMemo(
    () => buildCards(data.workoutPlan?.workout),
    [data.workoutPlan?.workout]
  );

  return (
    <Box component="section" sx={styles.section}>
      <SectionHeader
        title="Workout plan"
        subtitle="Personalized workouts and their target systems."
      />
      {cards.length === 0 ? (
        <Box sx={styles.subtitle(theme)}>
          No workout plan is available for this client yet.
        </Box>
      ) : (
        <Box sx={styles.grid}>
          {cards.map((card) => (
            <TrainingCard key={card.id} card={card} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TrainingPanel;

const styles = {
  section: {
    width: "100%",
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
  },
  // Empty-state fallback text reuses subtitle typography. Kept local because
  // it's only referenced from one place; `SectionHeader` handles the real
  // header block.
  subtitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: 1.6,
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "16px",
  },
  card: (theme: Theme) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column" as const,
    minWidth: 0,
  }),
  body: {
    padding: "12px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "4px",
    flex: 1,
  },
  category: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: 1.4,
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  name: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: 1.6,
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  pillsRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "8px",
    alignItems: "center",
    marginTop: "4px",
  },
  pill: (theme: Theme) => ({
    borderRadius: "8px",
    padding: "4px 8px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: 1.4,
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    whiteSpace: "nowrap" as const,
  }),
};
